'use client';

import React, { useState, useEffect, useRef } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import AppLogo from '@/components/ui/AppLogo';
import Icon from '@/components/ui/AppIcon';

const navLinks = [
  { label: 'Home', href: '/' },
  { label: 'About Us', href: '/about' },
  {
    label: 'Services',
    href: '/services',
    dropdown: [
      { label: 'Staffing & Recruitment', href: '/services' },
      { label: 'Temporary Staffing', href: '/services' },
      { label: 'Permanent Recruitment', href: '/services' },
      { label: 'Security Staffing', href: '/services' },
      { label: 'Workforce Management', href: '/services' },
      { label: 'HR Support', href: '/services' },
    ],
  },
  { label: 'For Employers', href: '/for-employers' },
  { label: 'For Candidates', href: '/for-candidates' },
  { label: 'Contact Us', href: '/contact' },
];

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const [mobileServicesOpen, setMobileServicesOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (mobileOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [mobileOpen]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setServicesOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <>
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 px-4 ${
          scrolled ? 'pt-3' : 'pt-5'
        }`}
      >
        <div className={`container max-w-7xl mx-auto transition-all duration-300 bg-white rounded-2xl ${
          scrolled ? 'shadow-nav py-1.5 px-4 lg:px-6' : 'shadow-lg py-2 px-4 lg:px-6'
        }`}>
          <div className="flex items-center justify-between">
            <Link href="/" className="flex items-center gap-3 group" aria-label="JK Staffing Home">
              <Image
                src="/assets/images/logo.jpg"
                alt="JK Staffing Logo"
                width={280}
                height={80}
                quality={100}
                priority
                className="object-contain w-auto h-14 lg:h-16"
              />
            </Link>

            <nav className="hidden lg:flex items-center gap-1" aria-label="Main navigation">
              {navLinks.map((link) =>
                link.dropdown ? (
                  <div key={link.label} className="relative" ref={dropdownRef}>
                    <button
                      onClick={() => setServicesOpen(!servicesOpen)}
                      className="nav-link-hover flex items-center gap-1 px-3 py-2 rounded-lg text-sm font-semibold text-foreground hover:text-secondary transition-colors duration-200"
                      aria-expanded={servicesOpen}
                      aria-haspopup="true"
                    >
                      {link.label}
                      <Icon name="ChevronDownIcon" size={14} className={`transition-transform duration-200 ${servicesOpen ? 'rotate-180' : ''}`} />
                    </button>
                    {servicesOpen && (
                      <div className="absolute top-full left-0 mt-2 w-56 bg-white rounded-2xl shadow-card-hover border border-border py-2 z-50">
                        {link.dropdown.map((item) => (
                          <Link
                            key={item.label}
                            href={item.href}
                            onClick={() => setServicesOpen(false)}
                            className="flex items-center gap-2 px-4 py-2.5 text-sm text-foreground hover:text-secondary hover:bg-muted transition-colors duration-150"
                          >
                            <span className="gold-dot" />
                            {item.label}
                          </Link>
                        ))}
                      </div>
                    )}
                  </div>
                ) : (
                  <Link
                    key={link.label}
                    href={link.href}
                    className="nav-link-hover px-3 py-2 rounded-lg text-sm font-semibold text-foreground hover:text-secondary transition-colors duration-200"
                  >
                    {link.label}
                  </Link>
                )
              )}
            </nav>

            <div className="hidden lg:flex items-center gap-3">
              <Link href="/for-candidates" className="btn-secondary text-sm py-2.5 px-5">
                Find a Job
              </Link>
              <Link href="/for-employers" className="btn-primary text-sm py-2.5 px-5">
                Request Staff
              </Link>
            </div>

            <button
              className="lg:hidden flex flex-col gap-1.5 p-2 rounded-lg"
              onClick={() => setMobileOpen(!mobileOpen)}
              aria-label={mobileOpen ? 'Close menu' : 'Open menu'}
              aria-expanded={mobileOpen}
            >
              <span className={`block w-6 h-0.5 bg-primary transition-all duration-300 ${mobileOpen ? 'rotate-45 translate-y-2' : ''}`} />
              <span className={`block w-6 h-0.5 bg-primary transition-all duration-300 ${mobileOpen ? 'opacity-0' : ''}`} />
              <span className={`block w-6 h-0.5 bg-primary transition-all duration-300 ${mobileOpen ? '-rotate-45 -translate-y-2' : ''}`} />
            </button>
          </div>
        </div>
      </header>

      {/* Mobile Drawer Overlay */}
      <div 
        className={`fixed inset-0 z-[100] flex justify-end lg:hidden transition-all duration-500 ${mobileOpen ? 'opacity-100 visible' : 'opacity-0 invisible pointer-events-none'}`}
      >
        <div
          className={`absolute inset-0 bg-[#0D2353]/70 backdrop-blur-sm transition-opacity duration-500 ${mobileOpen ? 'opacity-100' : 'opacity-0'}`}
          onClick={() => setMobileOpen(false)}
          aria-hidden="true"
        />
        
        {/* Mobile Drawer Panel */}
        <div 
          className={`relative w-[85%] max-w-[320px] h-full bg-white shadow-2xl flex flex-col transition-transform duration-500 cubic-bezier(0.16, 1, 0.3, 1) ${mobileOpen ? 'translate-x-0' : 'translate-x-full'}`}
          style={{ transitionTimingFunction: 'cubic-bezier(0.16, 1, 0.3, 1)' }}
        >
          {/* Header of Drawer */}
          <div className="flex items-center justify-between p-5 border-b border-gray-100 shrink-0">
            <Link href="/" onClick={() => setMobileOpen(false)} className="flex items-center">
              <Image
                src="/assets/images/logo.jpg"
                alt="JK Staffing Logo"
                width={160}
                height={45}
                quality={100}
                className="object-contain w-auto h-9 mix-blend-multiply"
              />
            </Link>
            <button
              onClick={() => setMobileOpen(false)}
              className="p-2 rounded-full bg-gray-50 hover:bg-gray-100 transition-colors"
              aria-label="Close menu"
            >
              <Icon name="XMarkIcon" size={20} className="text-[#0D2353]" />
            </button>
          </div>
          
          {/* Scrollable Navigation */}
          <div className="flex-1 overflow-y-auto py-6 px-4">
            <nav className="flex flex-col gap-2" aria-label="Mobile navigation">
              {navLinks.map((link, idx) => (
                <div 
                  key={link.label}
                  className="transition-all duration-500"
                  style={{ 
                    opacity: mobileOpen ? 1 : 0, 
                    transform: mobileOpen ? 'translateY(0)' : 'translateY(10px)',
                    transitionDelay: `${mobileOpen ? 100 + (idx * 50) : 0}ms`
                  }}
                >
                  {link.dropdown ? (
                    <button
                      onClick={() => setMobileServicesOpen(!mobileServicesOpen)}
                      className="flex items-center justify-between w-full px-5 py-4 rounded-xl text-base font-bold text-[#0D2353] hover:bg-[#F8FAFD] hover:text-[#FF6B2C] transition-colors"
                    >
                      {link.label}
                      <Icon name="ChevronDownIcon" size={16} className={`text-gray-400 transition-transform duration-300 ${mobileServicesOpen ? 'rotate-180' : ''}`} />
                    </button>
                  ) : (
                    <Link
                      href={link.href}
                      onClick={() => setMobileOpen(false)}
                      className="flex items-center justify-between w-full px-5 py-4 rounded-xl text-base font-bold text-[#0D2353] hover:bg-[#F8FAFD] hover:text-[#FF6B2C] transition-colors"
                    >
                      {link.label}
                      <Icon name="ChevronRightIcon" size={16} className="text-gray-300" />
                    </Link>
                  )}
                  {link.dropdown && (
                    <div 
                      className={`overflow-hidden transition-all duration-300 ease-in-out ${mobileServicesOpen ? 'max-h-[400px] opacity-100' : 'max-h-0 opacity-0'}`}
                    >
                      <div className="ml-6 pl-4 mt-1 mb-3 border-l-2 border-gray-100 flex flex-col gap-2">
                        {link.dropdown.map((item) => (
                          <Link
                            key={item.label}
                            href={item.href}
                            onClick={() => {
                              setMobileOpen(false);
                              setMobileServicesOpen(false);
                            }}
                            className="flex items-center px-4 py-3 rounded-xl text-sm font-semibold text-gray-500 hover:text-[#FF6B2C] hover:bg-[#F8FAFD] transition-colors"
                          >
                            {item.label}
                          </Link>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </nav>
          </div>

          {/* Bottom Actions */}
          <div 
            className="p-5 border-t border-gray-100 shrink-0 flex flex-col gap-3 bg-gray-50 pb-10 transition-all duration-700"
            style={{ 
              opacity: mobileOpen ? 1 : 0, 
              transform: mobileOpen ? 'translateY(0)' : 'translateY(20px)',
              transitionDelay: mobileOpen ? '400ms' : '0ms'
            }}
          >
            <Link href="/for-employers" onClick={() => setMobileOpen(false)} className="w-full bg-[#0D2353] text-white font-bold py-4 rounded-xl text-center hover:bg-[#1A4B9E] transition-all shadow-[0_8px_20px_rgba(13,35,83,0.15)] hover:shadow-[0_8px_25px_rgba(13,35,83,0.25)] hover:-translate-y-0.5">
              Request Staff
            </Link>
            <Link href="/for-candidates" onClick={() => setMobileOpen(false)} className="w-full bg-white text-[#0D2353] border border-gray-200 font-bold py-4 rounded-xl text-center hover:border-[#0D2353] hover:bg-gray-50 transition-colors shadow-sm">
              Find a Job
            </Link>
          </div>
        </div>
      </div>
    </>
  );
}